import { useCallback, useMemo } from "react";
import "../../css/FullEvent/BuyBanner.css";
import {
  CustomTicketV2,
  Event,
  PromoCode,
  TicketType,
  TicketV2,
} from "@markit/common.types";
import {
  isEventRelatedType,
  isPaidTicketType,
  isProductSpecificType,
} from "@markit/common.utils";
import { useTheme } from "../../hooks/useTheme";
import { atCapacityEventLabel } from "../../utils/eventUtils/eventUtils";
import RectangleButton from "../Buttons/RectangleButton";
import TextHoverButton from "../Buttons/TextHoverButton";

type BuyBannerProps = {
  event: Event;
  ticketData: TicketV2 | undefined;
  promoCode: PromoCode | undefined;
  isEventAtCapacity: boolean;
  setShowCheckoutForm: (showCheckoutForm: boolean) => void;
  visibleTickets: CustomTicketV2[];
  loadingState: boolean;
};

const BuyBanner = ({
  event,
  ticketData,
  promoCode,
  isEventAtCapacity,
  setShowCheckoutForm,
  visibleTickets,
  loadingState,
}: BuyBannerProps) => {
  const { theme } = useTheme();

  const alreadyRedeemed = useMemo(
    () =>
      ticketData &&
      (ticketData.redeemedBy !== "" ||
        ticketData.scanned ||
        // need the redeemeedBy field as well for the case where
        // the user shares their mass text ticket with someone else;
        // without it, the receiver won't be able to redeem it
        (ticketData.redeemedBy !== "" && ticketData.uid !== "")),
    [ticketData]
  );

  // Checks if the buy banner should be disabled, if the event is set to manually sold out,
  // and if the ticket is not a redeem ticket.
  const isEventSoldOut = useMemo(
    () => (isEventAtCapacity || event.isManualSoldOut) && !ticketData,
    [event.isManualSoldOut, isEventAtCapacity, ticketData]
  );

  // The event is over if the type is event related and the end date is in the past
  const isEventOver = useMemo(
    () =>
      isEventRelatedType(event.eventType) && new Date(event.end) < new Date(),
    [event.end, event.eventType]
  );

  const notYetRedeemed = useMemo(
    () => ticketData && ticketData.redeemedBy === "" && !ticketData.scanned,
    [ticketData]
  );

  const bannerSubText = useMemo(() => {
    if (isEventSoldOut && !event.isManualSoldOut) {
      return isProductSpecificType(event.eventType)
        ? "Redeem an Order"
        : "Redeem a Ticket";
    } else if (
      promoCode &&
      visibleTickets.some((ticket) => ticket.price !== 0)
    ) {
      return "Redeem your promo code";
    }
  }, [event, isEventSoldOut, promoCode, visibleTickets]);

  const btnText = useMemo(() => {
    if (alreadyRedeemed) {
      return "Redeemed";
    } else if (notYetRedeemed) {
      return "Redeem";
    } else if (isEventOver) {
      return "Event Over";
    } else if (isEventSoldOut) {
      return `${atCapacityEventLabel(event)}`;
    } else if (promoCode) {
      return "Redeem Code";
    } else if (
      visibleTickets.every((ticket) => ticket.type === TicketType.REQUEST)
    ) {
      // If all tickets are request, show request
      return "Request";
    } else if (!visibleTickets.some((ticket) => isPaidTicketType(ticket))) {
      // If no tickets are paid, show RSVP
      return isProductSpecificType(event.eventType) ? "Get Now" : "RSVP";
    } else {
      // (PETER): For Seaav, wanted to display this text for their event product pages as temp solution until we add paid products
      if (
        isProductSpecificType(event.eventType) ||
        event.id === "uHNHrHS-h" ||
        event.id === "agutp9-oJx"
      ) {
        return "Buy Now";
      }
      return "Get Tickets";
    }
  }, [
    alreadyRedeemed,
    event,
    isEventOver,
    isEventSoldOut,
    notYetRedeemed,
    promoCode,
    visibleTickets,
  ]);

  const disableBuyBtn = useMemo(() => {
    if (alreadyRedeemed || isEventOver || isEventSoldOut) {
      return true;
    }
    return false;
  }, [alreadyRedeemed, isEventOver, isEventSoldOut]);

  const showRedeemTicketSubtext = useMemo(
    () => isEventSoldOut && !event.isManualSoldOut && !isEventOver,
    [event.isManualSoldOut, isEventOver, isEventSoldOut]
  );

  const onBuyClick = useCallback(() => {
    if (event.locationBasedRSVP) {
      alert("This event requires you to download the app.");
      return;
    }
    window.scrollTo(0, 0);
    setShowCheckoutForm(true);
  }, [event.locationBasedRSVP, setShowCheckoutForm]);

  return (
    <div className="ColumnCentering" style={{ gap: 7 }}>
      <RectangleButton
        buttonLabel={btnText}
        onPress={onBuyClick}
        disabled={disableBuyBtn}
        loading={loadingState}
        theme={theme}
        altColor={theme.ButtonBG.backgroundColor}
        altTextColor={theme.ButtonText.color}
        altPaddingVert={14}
        altPaddingHorz={10}
        altBorderRadius={14}
      />
      {showRedeemTicketSubtext && bannerSubText ? (
        <TextHoverButton
          text={bannerSubText}
          onPress={onBuyClick}
          textStyles={{ ...theme.PrimaryText }}
        />
      ) : null}
    </div>
  );
};

export default BuyBanner;
